import { useCallback, useEffect, useState } from 'react';
import type { UrlDiscoveryResult } from '../hooks/useUrlDiscovery';
import { useUrlDiscovery } from '../hooks/useUrlDiscovery';
import styles from './UrlDiscoveryStep.module.css';

interface UrlDiscoveryStepProps {
  programId: string;
  baciCode: string;
  onComplete: () => void;
  onStepComplete: (completed: boolean) => void;
  onRegisterRunner?: (runner: (() => Promise<void>) | null) => void;
  onLoadingChange?: (isLoading: boolean) => void;
}

export function UrlDiscoveryStep({
  programId,
  baciCode,
  onComplete,
  onStepComplete,
  onRegisterRunner,
  onLoadingChange
}: UrlDiscoveryStepProps) {
  const { discoverUrls, isLoading, result, error, reset } = useUrlDiscovery();
  const [showToolTrace, setShowToolTrace] = useState(false);

  const handleDiscover = useCallback(async () => {
    try {
      const discovery = await discoverUrls(programId, baciCode);
      const hasVerified = Object.values(discovery.verified_urls ?? {}).some(Boolean);
      onStepComplete(hasVerified);
    } catch (err) {
      console.error('[QC] URL discovery failed', err);
      onStepComplete(false);
    }
  }, [discoverUrls, programId, baciCode, onStepComplete]);

  useEffect(() => {
    onLoadingChange?.(isLoading);
  }, [isLoading, onLoadingChange]);

  useEffect(() => {
    if (!onRegisterRunner) {
      return;
    }

    onRegisterRunner(() => handleDiscover());
    return () => {
      onRegisterRunner(null);
    };
  }, [handleDiscover, onRegisterRunner]);

  const handleContinue = () => {
    onStepComplete(true);
    onComplete();
  };

  const handleReset = () => {
    reset();
    onStepComplete(false);
  };

  const verifiedCount = result
    ? Object.values(result.verified_urls ?? {}).filter(Boolean).length
    : 0;
  const discoveredCount = result
    ? Object.values(result.discovered_urls ?? {}).filter(Boolean).length
    : 0;
  const failureCount = result?.verification_failures.length ?? 0;

  return (
    <section className={styles.container}>
      {isLoading && (
        <div className={styles.loadingState}>
          <div className={styles.spinner} />
          <p>Discovering URLs via BAM… this can take up to two minutes.</p>
        </div>
      )}

      {result && !isLoading && (
        <div className={styles.resultsState}>
          <div className={styles.statsRow}>
            <div className={styles.statCard}>
              <span className={styles.statLabel}>Discovered</span>
              <span className={styles.statValue}>{discoveredCount}</span>
            </div>
            <div className={styles.statCard}>
              <span className={styles.statLabel}>Verified</span>
              <span className={styles.statValue}>{verifiedCount}</span>
            </div>
            <div className={styles.statCard}>
              <span className={styles.statLabel}>Failures</span>
              <span className={styles.statValue}>{failureCount}</span>
            </div>
          </div>

          {verifiedCount > 0 && (
            <section className={styles.verifiedBlock}>
              <h4>Verified URLs (saved)</h4>
              <ul>
                {Object.entries(result.verified_urls)
                  .filter(([_, value]) => Boolean(value))
                  .map(([key, value]) => (
                    <li key={key}>
                      <strong>{key}:</strong>{' '}
                      <a href={value ?? ''} target="_blank" rel="noopener noreferrer">
                        {value}
                      </a>
                    </li>
                  ))}
              </ul>
            </section>
          )}

          {failureCount > 0 && (
            <section className={styles.failuresBlock}>
              <h4>Needs manual follow-up</h4>
              <ul>
                {result.verification_failures.map((failure, index) => (
                  <li key={index}>
                    <strong>{failure.field}:</strong> {failure.reason}
                    <br />
                    <span className={styles.failureUrl}>URL: {failure.url}</span>
                    {failure.status && <span className={styles.failureStatus}> (HTTP {failure.status})</span>}
                  </li>
                ))}
              </ul>
            </section>
          )}

          <div className={styles.resultsActions}>
            <button type="button" onClick={() => setShowToolTrace(true)} className={styles.secondaryButton}>
              Tool trace ({result.tool_executions.length})
            </button>
            <button type="button" onClick={handleReset} className={styles.mutedButton}>
              Reset results
            </button>
            <button type="button" onClick={handleContinue} className={styles.primaryButton}>
              Continue
            </button>
          </div>
        </div>
      )}

      {error && (
        <div className={styles.errorBanner}>
          <p>{error}</p>
        </div>
      )}

      {showToolTrace && result && (
        <ToolTraceModal
          executions={result.tool_executions}
          iterations={result.iteration_history}
          onClose={() => setShowToolTrace(false)}
        />
      )}
    </section>
  );
}

interface ToolTraceModalProps {
  executions: UrlDiscoveryResult['tool_executions'];
  iterations: UrlDiscoveryResult['iteration_history'];
  onClose: () => void;
}

function ToolTraceModal({ executions, iterations, onClose }: ToolTraceModalProps) {
  return (
    <div className={styles.modalBackdrop} onClick={onClose}>
      <div className={styles.modalContent} onClick={(event) => event.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h3>Tool execution trace</h3>
          <button type="button" onClick={onClose}>
            ✕
          </button>
        </div>
        <div className={styles.modalBody}>
          <section>
            <h4>Tool executions ({executions.length})</h4>
            <ul className={styles.toolList}>
              {executions.map((execution, index) => (
                <li key={index}>
                  <strong>{execution.name}</strong> — {execution.success ? 'success' : execution.error ?? 'failed'} ({(execution.duration_ms / 1000).toFixed(1)}s)
                </li>
              ))}
            </ul>
          </section>
          <section>
            <h4>Iterations ({iterations.length})</h4>
            <ul className={styles.toolList}>
              {iterations.map((snapshot) => (
                <li key={snapshot.iteration}>
                  <strong>Iteration {snapshot.iteration}</strong>
                  {snapshot.actions.length > 0 && (
                    <div className={styles.iterationList}>
                      <span>Actions: {snapshot.actions.join(', ')}</span>
                    </div>
                  )}
                  {snapshot.findings.length > 0 && (
                    <div className={styles.iterationList}>
                      <span>Findings: {snapshot.findings.join(', ')}</span>
                    </div>
                  )}
                  {snapshot.actions.length === 0 && snapshot.findings.length === 0 && (
                    <div className={styles.iterationList}>No recorded actions or findings.</div>
                  )}
                </li>
              ))}
            </ul>
          </section>
        </div>
      </div>
    </div>
  );
}
